import SearchBar from "../../common/components/SearchBar";
import SelectBox from "../../common/components/SelectBox";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import { useState } from "react";
import Table from "../../common/components/Table";
import {
  CommonCodeGroupSearchType,
  CommonCodeGroupSortKey,
  GetCommonCodeGroupsAsAdminDocument,
  useDeleteCommonCodeGroupAsAdminMutation,
  useGetCommonCodeGroupsAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import { CodeGroup } from "../container/CodeContainer";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import { useModal } from "../../../hooks/useModal";
import BasicButton from "../../common/components/BasicButton";
import UpdateCodeGroupModal from "./UpdateCodeGroupModal";
import { SortDirection } from "global";
import { useForm } from "../../../hooks/useForm";
import ActiveStatus from "../../common/components/ActiveStatus";

type QueryFilter = {
  query: string;
  commonCodeGroupSearchType: CommonCodeGroupSearchType;
  sortKey: CommonCodeGroupSortKey;
  sortDirection: "ASC" | "DESC";
};

interface CodeGroupBoxProps {
  group: CodeGroup | null;
  onGroupSelect: (group: CodeGroup) => void;
}

export default function CodeGroupBox({
  group: groupState,
  onGroupSelect,
}: CodeGroupBoxProps) {
  const [
    isUpdateModalOpen,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useModal();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [filters, onFilterChange, onBatchChange] = useForm({
    query: "",
    sortDirection: "DESC" as SortDirection,
    sortKey: CommonCodeGroupSortKey.CreatedAt || null,
    commonCodeGroupSearchType: CommonCodeGroupSearchType.Name || "",
  });

  const { data, loading, refetch } = useGetCommonCodeGroupsAsAdminQuery({
    variables: {
      first: FETCH_SIZE,
    },
  });

  const onSearch = () => {
    refetch({
      first: FETCH_SIZE,
      query: filters.query,
      sortKey: filters.sortKey,
      sortDirection: filters.sortDirection,
      commonGroupCodeSearchType:
        filters.commonCodeGroupSearchType || CommonCodeGroupSearchType.Name,
    });
  };

  const onSort = (key?: CommonCodeGroupSortKey, direction?: SortDirection) => {
    const updatedFilters: QueryFilter = {
      ...filters,
      sortKey: !key ? CommonCodeGroupSortKey.CreatedAt : key,
      sortDirection: !direction ? "DESC" : direction,
    };

    onBatchChange(updatedFilters);
    refetch({
      ...updatedFilters,
    });
  };

  const [deleteCodeGroup] = useDeleteCommonCodeGroupAsAdminMutation({
    refetchQueries: [
      {
        query: GetCommonCodeGroupsAsAdminDocument,
        variables: {
          first: FETCH_SIZE,
          ...filters,
        },
      },
    ],
  });

  const onDeleteCodeGroup = (groupId: string) => {
    const confirmed = window.confirm("삭제하시겠습니까?");

    if (!confirmed) {
      return;
    }

    deleteCodeGroup({
      variables: {
        commonCodeGroupId: groupId,
      },
    });
  };

  const onUpdateCodeGroup = (groupId: null | string) => {
    openUpdateModal();
    setSelectedGroupId(groupId);
  };

  const codeGroups = data?.getCommonCodeGroupsAsAdmin.edges ?? [];

  return (
    <>
      {isUpdateModalOpen && (
        <UpdateCodeGroupModal
          groupId={selectedGroupId}
          open={isUpdateModalOpen}
          onClose={closeUpdateModal}
        />
      )}
      <BoxWithShadow className="gap-3">
        <p className="font-bold">코드 그룹</p>
        <ListPageTopSection>
          <SelectBox
            value={filters.commonCodeGroupSearchType}
            onSelect={(value) =>
              onFilterChange("commonCodeGroupSearchType", value)
            }
            options={[
              {
                label: "그룹명",
                value: CommonCodeGroupSearchType.Name,
              },
              {
                label: "그룹코드",
                value: CommonCodeGroupSearchType.GroupCode,
              },
            ]}
          />
          <SearchBar
            placeholder="검색 내용"
            value={filters.query}
            onSearch={() => onSearch()}
            onChange={(value) => onFilterChange("query", value)}
          />
          <div className="ml-auto">
            <BasicButton
              text="그룹 추가"
              onClick={() => onUpdateCodeGroup(null)}
            />
          </div>
        </ListPageTopSection>
        <Table.Wrapper>
          <Table.Header
            tabs={[
              {
                label: "그룹코드",
                sortKey: CommonCodeGroupSortKey.GroupCode,
                className: "w-1/4 flex-shrink-0",
              },
              {
                label: "그룹명",
                sortKey: CommonCodeGroupSortKey.Name,
                className: "w-1/4 flex-shrink-0",
              },
              { label: "상태", sortKey: CommonCodeGroupSortKey.Status },
              { label: "등록일자", sortKey: CommonCodeGroupSortKey.CreatedAt },
              { label: "관리" },
            ]}
            sort={{
              key: filters.sortKey,
              direction: filters.sortDirection,
              onSort,
            }}
          />
          <Table.Body isLoading={loading}>
            {codeGroups.map((group) => {
              const isSelected = group.id === groupState?.id;

              return (
                <Table.Row
                  key={group.id}
                  isSelected={isSelected}
                  onClick={() =>
                    onGroupSelect({
                      id: group.id,
                      name: group.name,
                      code: group.groupCode,
                    })
                  }
                >
                  <Table.Column className="line-clamp-1 w-1/4 flex-shrink-0 text-ellipsis text-center">
                    {group.groupCode}
                  </Table.Column>
                  <Table.Column className="line-clamp-1 w-1/4 flex-shrink-0 text-ellipsis text-center">
                    {group.name}
                  </Table.Column>
                  <Table.Column>
                    <ActiveStatus status={group.status} />
                  </Table.Column>
                  <Table.Column>
                    {formatDate(group.createdAt, "YYYY.MM.DD")}
                  </Table.Column>
                  <Table.RowManageButtons
                    onDelete={
                      group.status === "INACTIVE"
                        ? undefined
                        : () => onDeleteCodeGroup(group.id)
                    }
                    onUpdate={() => onUpdateCodeGroup(group.id)}
                  />
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Wrapper>
      </BoxWithShadow>
    </>
  );
}
